// src/pages/proveedoresPage.js
import React, { useEffect, useState } from 'react';
import { Table, Button, Tag, Space, Input, Select, message, Tooltip, Modal, Descriptions, Divider } from 'antd';
import { PlusOutlined, ReloadOutlined, SearchOutlined, BookOutlined, EditOutlined, InboxOutlined, EyeOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { getAllSuppliers, searchSuppliersByName } from '../services/supplierService';
import { getAllBrands, getAllCategories } from '../services/brandsService';
import { getPedidoById, getAllPedidos, getPedidosBySupplier } from '../services/pedidosService';
import SupplierCreationModal from '../components/suppliers/SupplierCreationModal';
import SupplierEditModal from '../components/suppliers/SupplierEditModal';
import SupplierOrdersModal from '../components/orders/SupplierOrdersModal';
import OrderAttachments from '../components/orders/OrderAttachments';
import SupplierSummaryCards from '../components/suppliers/SupplierSummaryCards';
import './proveedoresPage.css';

const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';

const { Option } = Select;

const ProveedoresPage = () => {
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
        total: 0,
    });

    // Brands and Categories for display
    const [brands, setBrands] = useState([]);
    const [categories, setCategories] = useState([]);

    // Filters
    const [filters, setFilters] = useState({
        searchTerm: '',
        condicionImpositiva: null,
    });

    // Modal state
    const [modalVisible, setModalVisible] = useState(false);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    
    // Supplier orders modal state
    const [supplierOrdersModalVisible, setSupplierOrdersModalVisible] = useState(false);
    const [preSelectedSupplierId, setPreSelectedSupplierId] = useState(null);
    
    // Order details modal state
    const [orderDetailsModalVisible, setOrderDetailsModalVisible] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [loadingOrderDetails, setLoadingOrderDetails] = useState(false);
    
    // Summary cards state
    const [orders, setOrders] = useState([]);
    const [summary, setSummary] = useState(null);
    const [selectedSupplierForSummary, setSelectedSupplierForSummary] = useState('all');
    const [loadingSummary, setLoadingSummary] = useState(false);

    // Fetch orders for summary cards
    const fetchOrdersForSummary = async () => {
        setLoadingSummary(true);
        try {
            let response;
            if (selectedSupplierForSummary === 'all' || !selectedSupplierForSummary) {
                // Fetch all orders with reasonable limit
                response = await getAllPedidos(1, 100); // Use 100 records for summary
            } else {
                // Fetch orders for specific supplier
                response = await getPedidosBySupplier(selectedSupplierForSummary, 1, 100);
            }
            
            console.log('Orders API Response:', response);
            
            if (response && response.success) {
                const ordersList = response.data?.orders || response.data?.pedidos || [];
                console.log('Orders List:', ordersList);
                setOrders(ordersList);
                
                // Extract summary from response
                const summaryData = response.data?.summary || response.summary || null;
                console.log('Summary Data:', summaryData);
                setSummary(summaryData);
            }
        } catch (error) {
            console.error('Error fetching orders for summary:', error);
            message.error('Error al cargar resumen de órdenes');
        } finally {
            setLoadingSummary(false);
        }
    };

    // Fetch suppliers from backend
    const fetchSuppliers = async (page = 1, pageSize = 20) => {
        setLoading(true);
        try {
            // If there's a search term, use search endpoint
            if (filters.searchTerm && filters.searchTerm.trim().length > 0) {
                const response = await searchSuppliersByName(filters.searchTerm, 500);
                
                if (response.results) {
                    let filteredResults = response.results;
                    
                    // Apply additional filters
                    if (filters.condicionImpositiva) {
                        filteredResults = filteredResults.filter(
                            supplier => supplier.condicion_impositiva === filters.condicionImpositiva
                        );
                    }
                    
                    setSuppliers(filteredResults);
                    setPagination({
                        current: 1,
                        pageSize: pageSize,
                        total: filteredResults.length,
                    });
                } else {
                    setSuppliers([]);
                    setPagination({ current: 1, pageSize: pageSize, total: 0 });
                }
            } else {
                // Use getAllSuppliers endpoint with pagination and filters
                const apiFilters = {};
                if (filters.condicionImpositiva) {
                    apiFilters.condicionImpositiva = filters.condicionImpositiva;
                }
                
                const response = await getAllSuppliers(page, pageSize, apiFilters);
                
                if (response && response.success) {
                    setSuppliers(response.suppliers);
                    setPagination({
                        current: response.pagination.current_page,
                        pageSize: response.pagination.page_size,
                        total: response.pagination.total_count,
                    });
                } else {
                    message.error('Error al cargar proveedores');
                    setSuppliers([]);
                }
            }
        } catch (error) {
            console.error('Error fetching suppliers:', error);
            message.error('Error al cargar proveedores del servidor');
            setSuppliers([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSuppliers();
        fetchOrdersForSummary();
        
        // Fetch brands and categories for display
        const fetchBrandsAndCategories = async () => {
            try {
                const [brandsData, categoriesData] = await Promise.all([
                    getAllBrands(),
                    getAllCategories()
                ]);
                setBrands(brandsData);
                setCategories(categoriesData);
            } catch (error) {
                console.error('Error fetching brands/categories:', error);
            }
        };
        
        fetchBrandsAndCategories();
    }, []);
    
    // Fetch orders when selected supplier changes
    useEffect(() => {
        fetchOrdersForSummary();
    }, [selectedSupplierForSummary]);

    // Handle table pagination change
    const handleTableChange = (paginationConfig) => {
        fetchSuppliers(paginationConfig.current, paginationConfig.pageSize);
    };

    // Handle filter changes
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    // Apply filters
    const handleApplyFilters = () => {
        fetchSuppliers(1, pagination.pageSize);
    };

    // Clear filters
    const handleClearFilters = () => {
        setFilters({
            searchTerm: '',
            condicionImpositiva: null,
        });
        // Fetch without filters
        setTimeout(() => fetchSuppliers(1, pagination.pageSize), 100);
    };

    // Handle supplier creation
    const handleSupplierCreated = (newSupplier) => {
        message.success(`Proveedor "${newSupplier.razon_social}" creado exitosamente`);
        fetchSuppliers(pagination.current, pagination.pageSize);
    };

    // Handle edit button click
    const handleEditClick = (supplier) => {
        setSelectedSupplier(supplier);
        setEditModalVisible(true);
    };

    // Handle supplier update
    const handleSupplierUpdated = (updatedSupplier) => {
        message.success(`Proveedor "${updatedSupplier.razon_social}" actualizado exitosamente`);
        fetchSuppliers(pagination.current, pagination.pageSize);
    };
    
    // Handle view supplier orders
    const handleViewSupplierOrders = (supplierId) => {
        setPreSelectedSupplierId(supplierId);
        setSupplierOrdersModalVisible(true);
    };
    
    // Handle open general supplier orders modal
    const handleOpenSupplierOrdersModal = () => {
        setPreSelectedSupplierId(selectedSupplierForSummary && selectedSupplierForSummary !== 'all' ? selectedSupplierForSummary : null);
        setSupplierOrdersModalVisible(true);
    };
    
    // Handle view order details
    const handleViewOrderDetails = async (orderId) => {
        setLoadingOrderDetails(true);
        setOrderDetailsModalVisible(true);
        try {
            const response = await getPedidoById(orderId);
            if (response && response.success && response.data) {
                const orderData = response.data.order || response.data;
                const items = response.data.items || orderData.items || [];
                setSelectedOrder({
                    ...orderData,
                    items: items
                });
            } else {
                message.error('Error al cargar detalles de la orden');
                setOrderDetailsModalVisible(false);
            }
        } catch (error) {
            console.error('Error fetching order details:', error);
            message.error('Error al cargar detalles de la orden');
            setOrderDetailsModalVisible(false);
        } finally {
            setLoadingOrderDetails(false);
        }
    };
    
    // Format price helper
    const formatPrice = (price) => {
        if (typeof price !== 'number' || isNaN(price)) return 'N/A';
        return `$ ${price.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;  
    };
    
    // Get status label
    const getStatusLabel = (status) => {
        const labels = {
            'PENDIENTE': 'Pendiente',
            'APROBADO': 'Aprobado',
            'EN_TRANSITO': 'En Tránsito',
            'PARCIAL': 'Parcial',
            'RECIBIDO': 'Recibido',
            'CANCELADO': 'Cancelado',
        };
        return labels[status] || status;
    };
    
    // Get status color
    const getStatusColor = (status) => {
        const colors = {
            'PENDIENTE': 'orange',
            'APROBADO': 'blue',
            'EN_TRANSITO': 'cyan',
            'PARCIAL': 'purple',
            'RECIBIDO': 'green',
            'CANCELADO': 'red',
        };
        return colors[status] || 'default';
    };

    // Get tax status color
    const getTaxStatusColor = (status) => {
        const statusColors = {
            'RESPONSABLE INSCRIPTO': 'blue',
            'MONOTRIBUTO': 'green',
            'EXENTO': 'orange',
            'CONSUMIDOR FINAL': 'purple',
            'NO ESPECIFICADO': 'warning',
        };
        return statusColors[status] || 'default';
    };

    // Get supplier status color (ACTIVO/INACTIVO)
    const getSupplierStatusColor = (status) => {
        return status === 'ACTIVO' ? 'success' : 'default';
    };

    // Format CUIT with hyphens or show Informal tag
    const formatCuit = (cuit, record) => {
        if (!cuit) return 'N/A';
        // Check if this is an informal supplier
        if (cuit.startsWith('INFORMAL-') || record?.created_without_cuit) {
            return null; // Will be handled in render
        }
        const cleaned = cuit.replace(/\D/g, '');
        if (cleaned.length === 11) {
            return `${cleaned.slice(0, 2)}-${cleaned.slice(2, 10)}-${cleaned.slice(10)}`;
        }
        return cuit;
    };

    // Table columns
    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 50,
        },
        {
            title: 'CUIT',
            dataIndex: 'documento_nro',
            key: 'documento_nro',
            width: 130,
            render: (cuit, record) => {
                // Check if this is an informal supplier
                if (cuit?.startsWith('INFORMAL-') || record?.created_without_cuit) {
                    return <Tag color="orange">Informal</Tag>;
                }
                return formatCuit(cuit, record);
            },
        },
        {
            title: 'Razón Social',
            dataIndex: 'razon_social',
            key: 'razon_social',
            ellipsis: true,
            render: (text) => <strong>{text}</strong>,
        },
        {
            title: 'Condición Impositiva',
            dataIndex: 'condicion_impositiva',
            key: 'condicion_impositiva',
            width: 180,
            render: (status) => (
                <Tag color={getTaxStatusColor(status)}>
                    {status}
                </Tag>
            ),
        },
        {
            title: 'Localidad',
            dataIndex: 'localidad',
            key: 'localidad',
            width: 120,
            ellipsis: true,
            render: (text) => text || '-',
        },
        {
            title: 'Provincia',
            dataIndex: 'provincia',
            key: 'provincia',
            width: 100,
            ellipsis: true,
            render: (text) => text || '-',
        },
        {
            title: 'Teléfono',
            dataIndex: 'telefono',
            key: 'telefono',
            width: 130,
            render: (text) => text || '-',
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            ellipsis: true,
            render: (text) => text || '-',
        },
        {
            title: 'Marcas',
            dataIndex: 'related_brands',
            key: 'related_brands',
            ellipsis: true,
            render: (brandIds) => {
                if (!brandIds || brandIds.length === 0) return '-';
                
                const colors = ['blue', 'cyan', 'geekblue', 'purple', 'magenta'];
                
                const brandNames = brandIds
                    .map(id => brands.find(b => b.value === id))
                    .filter(Boolean);
                
                if (brandNames.length === 0) return '-';
                
                // Show first 2 brands, rest in tooltip
                const displayBrands = brandNames.slice(0, 2);
                const remainingCount = brandNames.length - 2;
                
                return (
                    <Space size={[0, 4]} wrap>
                        {displayBrands.map((brand, idx) => {
                            const colorIndex = brands.findIndex(b => b.value === brand.value) % colors.length;
                            return (
                                <Tag key={idx} color={colors[colorIndex]}>{brand.label}</Tag>
                            );
                        })}
                        {remainingCount > 0 && (
                            <Tooltip title={brandNames.slice(2).map(b => b.label).join(', ')}>
                                <Tag color="volcano">+{remainingCount} más</Tag>
                            </Tooltip>
                        )}
                    </Space>
                );
            },
        },
        {
            title: 'Categorías',
            dataIndex: 'related_categories',
            key: 'related_categories',
            ellipsis: true,
            render: (categoryIds) => {
                if (!categoryIds || categoryIds.length === 0) return '-';
                
                const categoryNames = categoryIds
                    .map(id => categories.find(c => c.value === id))
                    .filter(Boolean)
                    .map(c => c.label);
                
                if (categoryNames.length === 0) return '-';
                
                // Show first 2 categories, rest in tooltip
                const displayCategories = categoryNames.slice(0, 2);
                const remainingCount = categoryNames.length - 2;
                
                return (
                    <Space size={[0, 4]} wrap>
                        {displayCategories.map((name, idx) => (
                            <Tag key={idx} color="green">{name}</Tag>
                        ))}
                        {remainingCount > 0 && (
                            <Tooltip title={categoryNames.slice(2).join(', ')}>
                                <Tag color="green">+{remainingCount} más</Tag>
                            </Tooltip>
                        )}
                    </Space>
                );
            },
        },
        {
            title: 'Estado',
            dataIndex: 'estado',
            key: 'estado',
            width: 80,
            render: (status) => (
                <Tag color={getSupplierStatusColor(status)}>
                    {status}
                </Tag>
            ),
        },
        {
            title: 'Acciones',
            key: 'actions',
            width: 140,
            fixed: 'right',
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="Ver proveedor">
                        <Button
                            type="default"
                            size="small"
                            icon={<InboxOutlined />}
                            onClick={() => {
                                if (isDemoMode) { message.info('Vista de órdenes de compra del proveedor'); return; }
                                handleViewSupplierOrders(record.id);
                            }}
                            style={{ borderColor: '#1890ff', color: '#1890ff' }}
                        />
                    </Tooltip>
                    <Tooltip title="Editar proveedor">
                        <Button
                            type="primary"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => {
                                if (isDemoMode) { message.info('Editar proveedor — razón social, contacto, marcas y categorías'); return; }
                                handleEditClick(record);
                            }}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <div className="proveedores-page-container">
            <div className="proveedores-header">
                <h1>
                    <BookOutlined /> Proveedores
                </h1>
                <Space>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                            if (isDemoMode) {
                                message.info('Alta de nuevo proveedor — disponible en la versión completa del sistema');
                                return;
                            }
                            setModalVisible(true);
                        }}
                    >
                        Nuevo Proveedor
                    </Button>
                    <Button
                        type="default"
                        icon={<ReloadOutlined />}
                        onClick={() => fetchSuppliers(pagination.current, pagination.pageSize)}
                    >
                        Actualizar
                    </Button>
                </Space>
            </div>

            {/* Summary Cards */}
            {summary && (
                <SupplierSummaryCards
                    summary={summary}
                    orders={orders}
                    suppliers={suppliers}
                    selectedSupplierId={selectedSupplierForSummary}
                    onSupplierChange={setSelectedSupplierForSummary}
                    onViewSupplier={handleOpenSupplierOrdersModal}
                    loading={loadingSummary}
                    isDemoMode={isDemoMode}
                />
            )}

            {/* Filters */}
            <div className="proveedores-filters">
                <Space wrap size="middle">
                    <Input
                        placeholder="Buscar por razón social..."
                        style={{ width: 300 }}
                        prefix={<SearchOutlined />}
                        value={filters.searchTerm}
                        onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                        onPressEnter={handleApplyFilters}
                        allowClear
                        disabled={isDemoMode}
                    />

                    <Select
                        placeholder="Condición Impositiva"
                        style={{ width: 220 }}
                        allowClear
                        value={filters.condicionImpositiva}
                        onChange={(value) => handleFilterChange('condicionImpositiva', value)}
                        disabled={isDemoMode}
                    >
                        <Option value="RESPONSABLE INSCRIPTO">Responsable Inscripto</Option>
                        <Option value="MONOTRIBUTO">Monotributo</Option>
                        <Option value="EXENTO">Exento</Option>
                        <Option value="CONSUMIDOR FINAL">Consumidor Final</Option>
                        <Option value="NO ESPECIFICADO">No Especificado (Informal)</Option>
                    </Select>

                    <Button type="primary" onClick={handleApplyFilters} disabled={isDemoMode}>
                        Buscar
                    </Button>
                    <Button onClick={handleClearFilters} disabled={isDemoMode}>
                        Limpiar
                    </Button>
                </Space>
            </div>

            {/* Table */}
            <div className="proveedores-table-container">
                <Table
                    columns={columns}
                    dataSource={suppliers}
                    rowKey="id"
                    loading={loading}
                    pagination={pagination}
                    onChange={handleTableChange}
                    size="middle"
                />
            </div>

            {/* Supplier Creation Modal */}
            <SupplierCreationModal
                visible={modalVisible}
                onClose={() => setModalVisible(false)}
                onSupplierCreated={handleSupplierCreated}
            />

            {/* Supplier Edit Modal */}
            <SupplierEditModal
                visible={editModalVisible}
                onClose={() => {
                    setEditModalVisible(false);
                    setSelectedSupplier(null);
                }}
                onSupplierUpdated={handleSupplierUpdated}
                supplier={selectedSupplier}
            />
            
            {/* Supplier Orders Modal */}
            <SupplierOrdersModal
                visible={supplierOrdersModalVisible}
                onClose={() => {
                    setSupplierOrdersModalVisible(false);
                    setPreSelectedSupplierId(null);
                }}
                preSelectedSupplierId={preSelectedSupplierId}
                onViewOrderDetails={handleViewOrderDetails}
            />
            
            {/* Order Details Modal */}
            <Modal
                title={
                    <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
                        <EyeOutlined style={{ marginRight: '8px' }} />
                        Detalles de la Orden de Compra
                    </div>
                }
                open={orderDetailsModalVisible}
                onCancel={() => {
                    setOrderDetailsModalVisible(false);
                    setSelectedOrder(null);
                }}
                footer={[
                    <Button 
                        key="close" 
                        onClick={() => {
                            setOrderDetailsModalVisible(false);
                            setSelectedOrder(null);
                        }}
                    >
                        Cerrar
                    </Button>
                ]}
                width={1000}
                loading={loadingOrderDetails}
            >
                {selectedOrder && (
                    <div>
                        {/* Order Header Information */}
                        <Descriptions 
                            bordered 
                            size="small" 
                            column={2}
                            labelStyle={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}
                        >
                            <Descriptions.Item label="Número de Orden" span={1}>
                                <strong style={{ fontSize: '16px' }}>{selectedOrder.order_number}</strong>
                            </Descriptions.Item>
                            <Descriptions.Item label="Estado" span={1}>
                                <Tag color={getStatusColor(selectedOrder.status)}>
                                    {getStatusLabel(selectedOrder.status)}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Proveedor" span={2}>
                                {selectedOrder.supplier_razon_social || selectedOrder.supplier_name || 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Fecha de Creación">
                                {selectedOrder.order_date ? new Date(selectedOrder.order_date).toLocaleDateString('es-AR') : 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Fecha de Entrega Estimada">
                                {selectedOrder.expected_delivery_date ? new Date(selectedOrder.expected_delivery_date).toLocaleDateString('es-AR') : 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Condición de Pago">
                                {selectedOrder.payment_terms || selectedOrder.payment_term || 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Método de Pago">
                                {selectedOrder.payment_method || 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Factura Proveedor" span={2}>
                                {selectedOrder.related_supplier_invoice || <span style={{ color: '#999' }}>No especificada</span>}
                            </Descriptions.Item>
                            {selectedOrder.discount > 0 && (
                                <Descriptions.Item label="Descuento Total" span={2}>
                                    <strong style={{ color: '#52c41a' }}>
                                        {formatPrice(selectedOrder.discount)}
                                    </strong>
                                </Descriptions.Item>
                            )}
                            {selectedOrder.notes && (
                                <Descriptions.Item label="Notas" span={2}>
                                    {selectedOrder.notes}
                                </Descriptions.Item>
                            )}
                        </Descriptions>

                        {/* Order Attachments Section */}
                        <Divider orientation="left">Archivos Adjuntos</Divider>
                        <OrderAttachments 
                            orderId={selectedOrder.id}
                            readOnly={true}
                            canDelete={false}
                        />

                        <Divider orientation="left">Resumen Financiero</Divider>
                        <Descriptions bordered size="small" column={2}>
                            <Descriptions.Item label="Subtotal">
                                {formatPrice(selectedOrder.subtotal)}
                            </Descriptions.Item>
                            <Descriptions.Item label="IVA">
                                {formatPrice(selectedOrder.iva)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Descuento">
                                {formatPrice(selectedOrder.discount)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Otros Impuestos">
                                {formatPrice(selectedOrder.other_taxes)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Total" span={2}>
                                <strong style={{ fontSize: '18px', color: '#1890ff' }}>
                                    {formatPrice(selectedOrder.total)}
                                </strong>
                            </Descriptions.Item>
                        </Descriptions>

                        <Divider orientation="left">Productos</Divider>
                        
                        {/* Order Items Table */}
                        {selectedOrder.items && selectedOrder.items.length > 0 ? (
                            <Table
                                dataSource={selectedOrder.items || []}
                                rowKey={(record) => `${record.product_id}-${record.id || Math.random()}`}
                                pagination={false}
                                size="small"
                                scroll={{ x: 900 }}
                                columns={[
                                    {
                                        title: 'Producto',
                                        dataIndex: 'product_description',
                                        key: 'product_description',
                                        width: 200,
                                        render: (text, record) => (
                                            <div>
                                                <div><strong>{text || record.product_name || 'Sin descripción'}</strong></div>
                                                {record.product_code && (
                                                    <div style={{ fontSize: '12px', color: '#888' }}>
                                                        Código: {record.product_code}
                                                    </div>
                                                )}
                                            </div>
                                        ),
                                    },
                                    {
                                        title: 'Cantidad',
                                        dataIndex: 'quantity',
                                        key: 'quantity',
                                        width: 80,
                                        align: 'center',
                                    },
                                    {
                                        title: 'Precio Unitario',
                                        dataIndex: 'unit_price',
                                        key: 'unit_price',
                                        width: 120,
                                        align: 'right',
                                        render: (price) => formatPrice(price),
                                    },
                                    {
                                        title: 'Precio Compra',
                                        dataIndex: 'bulk_price',
                                        key: 'bulk_price',
                                        width: 120,
                                        align: 'right',
                                        render: (price, record) => {
                                            const basePrice = price || record.unit_price;
                                            return (
                                                <div>
                                                    <div>{formatPrice(basePrice)}</div>
                                                    {price && price !== record.unit_price && (
                                                        <div style={{ fontSize: '11px', color: '#1890ff' }}>
                                                            (Precio especial)
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        },
                                    },
                                    {
                                        title: 'Descuento %',
                                        dataIndex: 'discount_percentage',
                                        key: 'discount_percentage',
                                        width: 100,
                                        align: 'center',
                                        render: (discount) => {
                                            const effectiveDiscount = discount || 0;
                                            return (
                                                <Tag color={discount > 0 ? 'gold' : 'default'}>
                                                    {effectiveDiscount}%
                                                </Tag>
                                            );
                                        },
                                    },
                                    {
                                        title: 'Precio c/Desc.',
                                        key: 'price_after_discount',
                                        width: 120,
                                        align: 'right',
                                        render: (_, record) => {
                                            const basePrice = record.bulk_price || record.unit_price;
                                            const discount = record.discount_percentage || 0;
                                            const priceAfterDiscount = basePrice * (1 - discount / 100);
                                            return formatPrice(priceAfterDiscount);
                                        },
                                    },
                                    {
                                        title: 'IVA %',
                                        dataIndex: 'iva_percentage',
                                        key: 'iva_percentage',
                                        width: 80,
                                        align: 'center',
                                        render: (iva) => `${iva || 0}%`,
                                    },
                                    {
                                        title: 'Subtotal',
                                        key: 'subtotal',
                                        width: 120,
                                        align: 'right',
                                        render: (_, record) => {
                                            const basePrice = record.bulk_price || record.unit_price;
                                            const discount = record.discount_percentage || 0;
                                            const priceAfterDiscount = basePrice * (1 - discount / 100);
                                            const subtotal = priceAfterDiscount * record.quantity;
                                            return formatPrice(subtotal);
                                        },
                                    },
                                ]}
                                summary={(pageData) => {
                                    let totalSubtotal = 0;
                                    let totalDiscount = 0;
                                    let totalBeforeDiscount = 0;
                                    let totalIVA = 0;

                                    pageData.forEach(item => {
                                        const basePrice = item.bulk_price || item.unit_price;
                                        const discount = item.discount_percentage || 0;
                                        const itemBeforeDiscount = basePrice * item.quantity;
                                        const priceAfterDiscount = basePrice * (1 - discount / 100);
                                        const subtotal = priceAfterDiscount * item.quantity;
                                        const itemIvaPercent = Number(item.iva_percentage) || 0;
                                        const itemIVA = subtotal * (itemIvaPercent / 100);
                                        
                                        totalBeforeDiscount += itemBeforeDiscount;
                                        totalSubtotal += subtotal;
                                        totalIVA += itemIVA;
                                    });

                                    totalDiscount = totalBeforeDiscount - totalSubtotal;
                                    const cargasOn = selectedOrder.cargas_impositivas_on;
                                    const effectiveIVA = cargasOn ? totalIVA : 0;
                                    const percepcion = Number(selectedOrder.percepcion) || 0;
                                    const iibbDirecto = Number(selectedOrder.iibb_directo) || 0;
                                    const retencion = Number(selectedOrder.retencion) || 0;
                                    const grandTotal = totalSubtotal + effectiveIVA + percepcion + iibbDirecto - retencion;

                                    return (
                                        <>
                                            <Table.Summary.Row style={{ backgroundColor: '#f0f0f0' }}>
                                                <Table.Summary.Cell index={0} colSpan={7}>
                                                    <strong>Subtotal (sin IVA)</strong>
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell index={1} align="right">
                                                    <strong>{formatPrice(totalSubtotal)}</strong>
                                                </Table.Summary.Cell>
                                            </Table.Summary.Row>
                                            {totalDiscount > 0 && (
                                                <Table.Summary.Row style={{ backgroundColor: '#f6ffed' }}>
                                                    <Table.Summary.Cell index={0} colSpan={7}>
                                                        <strong style={{ color: '#52c41a' }}>
                                                            Ahorro total por descuentos
                                                        </strong>
                                                    </Table.Summary.Cell>
                                                    <Table.Summary.Cell index={1} align="right">
                                                        <strong style={{ color: '#52c41a' }}>
                                                            -{formatPrice(totalDiscount)}
                                                        </strong>
                                                    </Table.Summary.Cell>
                                                </Table.Summary.Row>
                                            )}
                                            <Table.Summary.Row style={{ backgroundColor: '#f0f0f0' }}>
                                                <Table.Summary.Cell index={0} colSpan={7}>
                                                    <strong>IVA Total</strong>
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell index={1} align="right">
                                                    <strong>{formatPrice(effectiveIVA)}</strong>
                                                </Table.Summary.Cell>
                                            </Table.Summary.Row>
                                            {(percepcion > 0 || iibbDirecto > 0 || retencion > 0) && (
                                                <>
                                                    {percepcion > 0 && (
                                                        <Table.Summary.Row>
                                                            <Table.Summary.Cell index={0} colSpan={7}>
                                                                Percepción
                                                            </Table.Summary.Cell>
                                                            <Table.Summary.Cell index={1} align="right">
                                                                +{formatPrice(percepcion)}
                                                            </Table.Summary.Cell>
                                                        </Table.Summary.Row>
                                                    )}
                                                    {iibbDirecto > 0 && (
                                                        <Table.Summary.Row>
                                                            <Table.Summary.Cell index={0} colSpan={7}>
                                                                IIBB Directo
                                                            </Table.Summary.Cell>
                                                            <Table.Summary.Cell index={1} align="right">
                                                                +{formatPrice(iibbDirecto)}
                                                            </Table.Summary.Cell>
                                                        </Table.Summary.Row>
                                                    )}
                                                    {retencion > 0 && (
                                                        <Table.Summary.Row>
                                                            <Table.Summary.Cell index={0} colSpan={7}>
                                                                Retención
                                                            </Table.Summary.Cell>
                                                            <Table.Summary.Cell index={1} align="right">
                                                                -{formatPrice(retencion)}
                                                            </Table.Summary.Cell>
                                                        </Table.Summary.Row>
                                                    )}
                                                </>
                                            )}
                                            <Table.Summary.Row style={{ backgroundColor: '#e6f7ff', fontSize: '16px' }}>
                                                <Table.Summary.Cell index={0} colSpan={7}>
                                                    <strong>TOTAL</strong>
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell index={1} align="right">
                                                    <strong style={{ fontSize: '18px', color: '#1890ff' }}>
                                                        {formatPrice(grandTotal)}
                                                    </strong>
                                                </Table.Summary.Cell>
                                            </Table.Summary.Row>
                                        </>
                                    );
                                }}
                            />
                        ) : (
                            <div style={{ 
                                padding: '40px 20px', 
                                textAlign: 'center', 
                                backgroundColor: '#fff7e6', 
                                border: '1px dashed #ffa940',
                                borderRadius: '4px',
                                marginTop: '16px'
                            }}>
                                <ExclamationCircleOutlined style={{ fontSize: '32px', color: '#fa8c16', marginBottom: '12px' }} />
                                <div style={{ fontSize: '14px', color: '#8c8c8c' }}>  
                                    Los detalles de productos no están disponibles en esta vista.
                                    <br />
                                    Visite la página de Órdenes de Compra para ver el detalle completo.
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default ProveedoresPage;
